import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, FileText, Mail, MessageSquare, User, Briefcase } from 'lucide-react';
import { EstimateProjectCustomer, EstimateToCustomerRequest } from './estimateType';
import { formatAddress, formatCurrency } from '../lib/global_function';
import EstimateApi from './estimateApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';

const EstimateToCustomerPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Send Estimate', path: '/main/project/estimate/tocustomer' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const estimate: EstimateProjectCustomer | undefined = location.state?.estimate;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSentSuccessfully, setIsSentSuccessfully] = useState(false);

  // Check if estimate is available as soon as component mounts
  useEffect(() => {
    if (!estimate) {
      setErrorMessage('Estimate not available, please click the Go To Estimate button to select an estimate.');
    }
  }, [estimate]);

  const handleView = async () => {
    if (!estimate) {
      setErrorMessage('No estimate selected');
      return;
    }
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const pdfBlob = await EstimateApi.viewEstimatePdf(estimate.estimate_id);
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to generate PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    setErrorMessage(null);
    setMessage(null);
    setIsLoading(true);
    setIsSentSuccessfully(false); // Reset the flag on each send attempt

    if (!estimate) {
      setErrorMessage('No estimate selected');
      setIsLoading(false);
      return;
    }

    if (!sendMethod) {
      setErrorMessage('Please select a send method');
      setIsLoading(false);
      return;
    }

    const accountId = getAccountIdFromSessionStorage((process.env.REACT_APP_SESSION_STORAGE_KEY as string));
    if (!accountId) {
      setErrorMessage('Account not available.');
      setIsLoading(false);
      return;
    }

    try {
      const requestBody: EstimateToCustomerRequest = {
        estimate_id: estimate.estimate_id,
        account_id: accountId,
        project_id: estimate.project_id,
        customer_id: estimate.customer_id,
        send_method: sendMethod === 'text' ? 'sms' : sendMethod,
        recipient_email: estimate.customer_email,
        recipient_phone: estimate.customer_phone,
        message_for: "estimate",
      };

      const response = await EstimateApi.sendEstimateToCustomer(requestBody);
      setIsSentSuccessfully(true); // Set the flag to true on successful API call
      setMessage('Your estimate PDF has been generated and is now being sent');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send estimate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToEstimateList = () => {
    navigate('/main/project/estimate');
  };

  // Helper function to safely format address when estimate might be undefined
  const safeFormatAddress = () => {
    if (!estimate) return '';
    return formatAddress(
      estimate.customer_address_1,
      estimate.customer_address_2,
      estimate.customer_city,
      estimate.customer_state,
      estimate.customer_zip
    );
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Send Estimate to Customer</h1>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
              {!estimate && (
                <Button onClick={handleNavigateToEstimateList} className="mt-3">
                  Go to Estimate
                </Button>
              )}
            </Alert>
          )}

          {/* Success Message */}
          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {estimate ? (
            <div className="max-w-4xl space-y-6">
              {/* Customer Information */}
              <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                <div className="flex items-center gap-3 mb-4">
                  <User className="size-6 text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">Customer Information</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-paragraph mb-1"><strong>Name:</strong>&nbsp;&nbsp; 
                      {estimate.customer_first_name} {estimate.customer_last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-paragraph mb-1"><strong>Address:</strong>&nbsp;&nbsp;
                    {safeFormatAddress()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-paragraph mb-1"><strong>Email:</strong>&nbsp;&nbsp;
                    {estimate.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-paragraph mb-1"><strong>Phone:</strong>&nbsp;&nbsp;
                     {estimate.customer_phone}</p>
                  </div>
                </div>
              </div>

              {/* Estimate Details */}
              <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Briefcase className="size-6 text-primary" />
                    <h2 className="text-xl font-semibold text-foreground">Estimate Details</h2>
                  </div>
                  <Button 
                    onClick={handleView} 
                    disabled={isLoading}
                    variant="inverted"
                    size="medium"
                  >
                    <FileText className="size-4" />
                    View PDF
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
  <p className="text-sm font-medium text-paragraph mb-1">
    <strong>Project:</strong>&nbsp;&nbsp;{estimate.project_name}
  </p>
</div>
<div>
  <p className="text-sm font-medium text-paragraph mb-1">
    <strong>Estimate Name:</strong>&nbsp;&nbsp;{estimate.name}
  </p>
</div>

                <div className="pt-4 border-t-2 border-muted">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Estimate Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="font-semibold">
                        ${formatCurrency(Number(estimate.total_dollar || 0).toFixed(2))}
                      </span>
                    </div>



{Number(estimate.tax_rate) > 0 && (
  <div className="flex justify-between text-sm">
    <span className="text-gray-700">Tax ({estimate.tax_rate}%):</span>
    <span className="font-semibold">
      ${formatCurrency(Number(estimate.tax_amount || 0).toFixed(2))}
    </span>
  </div>
)}


                    {Number(estimate.discount_usd) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Discount:</span>
                        <span className="font-semibold text-red-600">
                          -${formatCurrency(Number(estimate.discount_usd || 0).toFixed(2))}
                        </span>
                      </div>
                    )}

                    <div className="pt-2 border-t border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-foreground">Total:</span>
                        <span className="text-2xl font-bold text-primary">
                          ${formatCurrency(Number(estimate.final_total || estimate.total_dollar || 0).toFixed(2))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>


                </div>
              </div>

              {/* Send Options */}
              {!isSentSuccessfully && (
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Send Estimate to Customer
                  </h2>

                  {/* Send Method Selection */}
                  <div className="space-y-4 mb-6">
                    <p className="text-sm text-paragraph">
                      Choose how you'd like to send the estimate:
                    </p>

                    <div className="space-y-3">
                      {/* Email Option */}
                      <label 
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          sendMethod === 'email' 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted hover:border-primary/30'
                        }`}
                      >
                        <input
                          type="radio"
                          value="email"
                          checked={sendMethod === 'email'}
                          onChange={() => setSendMethod('email')}
                          className="mt-1 size-4 text-primary cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="size-5 text-primary" />
                            <span className="font-semibold text-foreground">Email</span>
                          </div>
                          {sendMethod === 'email' && (
                            <p className="text-sm text-paragraph mt-2">
                              The estimate will be sent as a PDF file attachment in an email from MrJoe@MrJoe.pro. 
                              By clicking Send Email, you confirm that the customer {estimate.customer_first_name} {estimate.customer_last_name} has 
                              agreed to receive this email to review the estimate you provided.
                            </p>
                          )}
                        </div>
                      </label>

                      {/* Text/SMS Option */}
                      <label 
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          sendMethod === 'text' 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted hover:border-primary/30'
                        }`}
                      >
                        <input
                          type="radio"
                          value="text"
                          checked={sendMethod === 'text'}
                          onChange={() => setSendMethod('text')}
                          className="mt-1 size-4 text-primary cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageSquare className="size-5 text-primary" />
                            <span className="font-semibold text-foreground">Text Message (SMS)</span>
                          </div>
                          {sendMethod === 'text' && (
                            <p className="text-sm text-paragraph mt-2">
                              The estimate will be sent as a link in a text message from (800)579-5808. 
                              By clicking Send Text, you confirm that the customer {estimate.customer_first_name} {estimate.customer_last_name} has 
                              agreed to receive this message to review the estimate you provided.
                            </p>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Send Button */}
                  <div className="flex justify-start">
                    <Button
                      onClick={handleSend}
                      disabled={isLoading || !sendMethod}
                      size="large"
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" fullScreen={false} className="size-5" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="size-5" />
                          Send {sendMethod ? (sendMethod === 'email' ? 'Email' : 'Text') : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            !errorMessage && (
              <div className="bg-white rounded-2xl border-2 border-muted p-12 text-center">
                <FileText className="size-16 mx-auto mb-4 text-muted" />
                <p className="text-paragraph">No estimate data available to display.</p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default EstimateToCustomerPage;